import {observer} from 'mobx-react-lite'
import React, {useEffect} from 'react'
import {Lang} from 'yougile-lang'
import {ObjUtil} from 'yougile-util'
import {DataObjectId, Hub} from 'yougile-model'
import {DepartmentsClient} from '@client/departments-client'
import {Sync} from '@client/sync'
import {Button, LinkButtonSmall} from '@components/common/button/button'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {DropdownContent} from '@components/common/dropdown/dropdown-content'
import {UsersTable} from '@components/common/users-table/users-table'
import {UsersTableUtil} from '@components/common/users-table/users-table-util'
import {getDataUsers} from '@components/sticker/user-sticker'
import {useBoolean} from '@containers/hooks'
import {PageService} from '@extensions/page-service'
import {Sticker} from '@extensions/sticker/sticker'
import {
  canAssignUser,
  getTasksOnUser,
  getUsersWithTasks
} from '@extensions/sticker/views-web/user-view'
import {useMultiActionsWithTasksStore} from '../multi-actions-with-tasks-store'

interface Props {
  task: Hub
  cb: (close?: boolean) => void
}

export const AssignedSticker = observer(
  React.forwardRef((props: Props, ref: React.MutableRefObject<HTMLElement>) => {
    const {cb} = props
    const [state, store] = useMultiActionsWithTasksStore()
    if (!state.tag) {
      return null
    }
    const box = Sync.get(state.tag)
    const assigned = getDataUsers(undefined)
    const proj = PageService.getCurrentProject()
    const users = Sync.getCurrentSandbox().getAllUsers(false, assigned)
    const calculatedHierarchy = DepartmentsClient.getHierarchy(box)
    const listUsers = users.filter(
      u =>
        assigned.includes(u.id) ||
        canAssignUser(proj, u.id, calculatedHierarchy)
    )
    const tasksFromMultiActions = Array.from(state.tasksIds)
    const usersWithTasks = getUsersWithTasks(
      listUsers,
      tasksFromMultiActions,
      state.tag
    )
    const tasksOnUser = getTasksOnUser(tasksFromMultiActions, usersWithTasks)
    const [selectedUsers, setUsers] = React.useState<DataObjectId[]>(
      ObjUtil.keys(
        ObjUtil.filter(
          tasksOnUser,
          (_userId, v) => !['some', 'none'].includes(v)
        )
      )
    )
    const open = useBoolean(true)
    useEffect(() => {
      !open.value && cb()
    }, [open.value])
    useEffect(() => {
      open.turnOn()
    }, [state.tag])
    const onPin = (users: DataObjectId[]) => {
      store.setAssignedUsers(users)
      cb(true)
    }
    const onSelectedChanged = (users: DataObjectId[]) => {
      setUsers(users.length ? users : [])
    }
    const unpin = () => {
      const box = Sync.getCurrentSandbox()
      for (const id of Array.from(state.tasksIds)) {
        const task = box.getHub(id)
        if (!task || !state.tag) {
          continue
        }
        Sticker.unpin(state.tag, task)
      }
      cb()
    }

    const submit = () => {
      onPin?.(selectedUsers)
      cb()
    }

    return (
      <Dropdown
        visible={open.value}
        onVisibleChange={() => cb(false)}
        content={
          <DropdownContent
            className="z-10 w-304 h-304"
            header={
              <>
                <div>{Lang.msg.multiActionsWithTasks.changeAssignees}</div>
                <LinkButtonSmall onClick={unpin}>
                  {Lang.msg.unpin}
                </LinkButtonSmall>
              </>
            }
            footerClassName="h-48"
            footer={
              proj && (
                <div className="w-full flex justify-end mr-4">
                  <Button onClick={submit}>
                    {Lang.msg.multiActionsWithTasks.confirm}
                  </Button>
                </div>
              )
            }
          >
            <UsersTable
              className="h-full"
              users={listUsers.map(u =>
                UsersTableUtil.convertUser(box, u, proj)
              )}
              selected={selectedUsers}
              onSelectedChanged={onSelectedChanged}
              useCheckbox
              tasksOnUser={tasksOnUser}
            />
          </DropdownContent>
        }
        reference={ref}
        hideArrow
        className="right-1/2"
      />
    )
  })
)
